<template>
    <kiosk-team-details :teamId="props.teamId">
        <div class="col-span-12 lg:col-span-10 flex flex-col w-full justify-center">
            <div class="max-w-(--breakpoint-sm) flex flex-col">
                <page-callout section="kiosk-team-billing-details" class="mb-4" />

                <page-header>Billing Details</page-header>

                <team-billing-email
                    class="mb-4"
                    :team-id="team.id"
                    :billing-email="team.billing_email"
                    :is-editable="isEditable"
                    :allow-unset="true"
                    :unsetting="unsettingEmail"
                    @unset="confirmUnsetEmail"
                />

                <team-billing-info
                    class="mb-4"
                    :team-id="team.id"
                    :billing-information="{
                        billing_company_legal_name: team.billing_company_legal_name,
                        billing_address: team.billing_address,
                        billing_address_line_2: team.billing_address_line_2,
                        billing_city: team.billing_city,
                        billing_state: team.billing_state,
                        billing_zip: team.billing_zip,
                        billing_country: team.billing_country,
                        billing_po_number: team.billing_po_number,
                    }"
                    :is-editable="isEditable"
                    :allow-unset="true"
                    :unsetting="unsettingInformation"
                    @unset="confirmUnsetInformation"
                />

                <team-stripe-customer v-if="stripeKey && isEditable" class="mb-4" :stripe-id="team.stripe_id" />
            </div>
        </div>
    </kiosk-team-details>
</template>

<script setup>
import { VxSwal } from '@voxie/frontend-components';
import { computed, ref } from 'vue';
import KioskTeamDetails from '~/components/kiosk/kiosk-teams/details/KioskTeamDetails.vue';
import { useTeamDetails } from '~/components/kiosk/kiosk-teams/composables/useTeamDetails';
import PageHeader from '~/components/general/PageHeader.vue';
import PageCallout from '~/components/general/PageCallout.vue';
import TeamBillingEmail from '~/components/settings/billing/TeamBillingEmail.vue';
import TeamBillingInfo from '~/components/settings/billing/TeamBillingInfo.vue';
import TeamStripeCustomer from '~/components/settings/billing/TeamStripeCustomer.vue';
import { useToasted } from '~/composables/useToasted';
import { useFormRequestHandler } from '~/composables/useFormRequestHandler';
import { adminTeamsClient } from '~/services';

const { team, refreshTeam } = useTeamDetails();

const props = defineProps({
    teamId: {
        type: Number,
        required: true,
    },
});

const stripeKey = Spark.stripeKey;
const isEditable = computed(() => team.value.status !== 'closed');

const toasted = useToasted();
const { catchError } = useFormRequestHandler();
const unsettingInformation = ref(false);
const unsettingEmail = ref(false);

const confirmUnsetInformation = () => {
    VxSwal.confirmation(
        'Unset billing information',
        'This permanently clears the billing address, company name, and PO number for this team. This action cannot be undone.',
        'danger',
        () => unsetInformation()
    );
};

const unsetInformation = async () => {
    if (unsettingInformation.value) {
        return;
    }

    unsettingInformation.value = true;
    try {
        await adminTeamsClient.unsetBillingInformation(props.teamId);
        await refreshTeam(props.teamId);
        toasted.success('Billing information cleared.');
    } catch (e) {
        catchError(e);
    } finally {
        unsettingInformation.value = false;
    }
};

const confirmUnsetEmail = () => {
    VxSwal.confirmation(
        'Unset billing email',
        'This permanently clears the billing email for this team. This action cannot be undone.',
        'danger',
        () => unsetEmail()
    );
};

const unsetEmail = async () => {
    if (unsettingEmail.value) {
        return;
    }

    unsettingEmail.value = true;
    try {
        await adminTeamsClient.unsetBillingEmail(props.teamId);
        await refreshTeam(props.teamId);
        toasted.success('Billing email cleared.');
    } catch (e) {
        catchError(e);
    } finally {
        unsettingEmail.value = false;
    }
};
</script>
